import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { startAuthentication } from "@simplewebauthn/browser";
import { Gavel, Fingerprint, PenLine, Printer, Loader2, CheckCircle2, Clock, Paperclip, Library, Download, Folder } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { SignaturePad } from "./SignaturePad";
import { api } from "../lib/api";
import { printDecision, decisionPDFBlob } from "../lib/decisionPrint";
import { passkeySupported } from "../lib/passkey";

const urlFor = (id) => api.portalFileUrl(id);

const SignBox = ({ d, onSigned }) => {
  const pad = useRef(null);
  const [busy, setBusy] = useState("");
  const [bioFailed, setBioFailed] = useState(!passkeySupported());
  const sign = async (withBio) => {
    const signature = pad.current?.getSignature();
    if (!signature) { toast.error("وقّع في المربع أولاً"); return; }
    setBusy(withBio ? "bio" : "sig");
    try {
      const body = { signature, origin: window.location.origin };
      if (withBio) {
        const o = await api.portalDecisionBioOptions(d.id, window.location.origin);
        body.credential = await startAuthentication({ optionsJSON: o.options });
        body.challenge_id = o.challenge_id;
      }
      const r = await api.portalSignDecision(d.id, body);
      toast.success("تم توقيع القرار ✅");
      onSigned(r);
    } catch (e) {
      if (withBio) setBioFailed(true);
      toast.error(e?.response?.data?.detail || (withBio ? "تعذّر التحقق بالبصمة" : "تعذّر التوقيع"));
    } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3 rounded-2xl border border-blue-200 bg-blue-50/50 p-3" data-testid="decision-sign-box">
      <p className="text-sm font-bold text-blue-900">التوقيع والبصمة</p>
      <SignaturePad ref={pad} height={150} />
      <Button data-testid="decision-sign-bio" onClick={() => sign(true)} disabled={!!busy || !passkeySupported()} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700">
        {busy === "bio" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-5 w-5" />} وقّع وأكّد ببصمتك
      </Button>
      {bioFailed && (
        <Button data-testid="decision-sign-only" variant="outline" onClick={() => sign(false)} disabled={!!busy} className="w-full gap-2">
          {busy === "sig" ? <Loader2 className="h-4 w-4 animate-spin" /> : <PenLine className="h-4 w-4" />} توقيع فقط (بدون بصمة)
        </Button>
      )}
      <p className="text-[11px] text-slate-500">البصمة تستخدم بصمة الإصبع أو الوجه في جوالك. فعّلها مرة واحدة من «بياناتي ← الدخول بالبصمة».</p>
    </div>
  );
};

export const PortalDecisions = () => {
  const [data, setData] = useState({ items: [], company: {} });
  const [cur, setCur] = useState(null);
  const load = useCallback(() => api.portalDecisions().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const c = data.company || {};
  return (
    <div className="space-y-2" data-testid="portal-decisions">
      {data.items.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400">لا توجد قرارات</p>}
      {data.items.map((d) => (
        <button key={d.id} data-testid={`portal-decision-${d.id}`} onClick={() => setCur(d)}
          className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-right shadow-sm transition hover:border-blue-300">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600"><Gavel className="h-5 w-5" /></div>
          <div className="min-w-0 flex-1"><p className="truncate font-semibold">{d.title}</p><p className="text-xs text-slate-500">#{d.number} · {d.date}</p></div>
          {d.status === "signed"
            ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[11px] font-bold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> موقّع</span>
            : d.require_signature ? <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-bold text-amber-700"><Clock className="h-3 w-3" /> بانتظار توقيعك</span> : null}
        </button>
      ))}
      <Dialog open={!!cur} onOpenChange={(o) => !o && setCur(null)}>
        <DialogContent dir="rtl" className="max-h-[92vh] max-w-2xl overflow-y-auto p-4" data-testid="portal-decision-dialog">
          {cur && <>
            <DialogHeader className="text-right"><DialogTitle>{cur.title}</DialogTitle><DialogDescription>قرار رقم {cur.number} · {cur.date}</DialogDescription></DialogHeader>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between border-b-2 border-slate-800/80 pb-2">
                <div><p className="font-heading font-extrabold">{c.name}</p>{c.cr_number && <p className="text-[11px] text-slate-500">س.ت: {c.cr_number}</p>}</div>
                {c.logo_id && <img src={urlFor(c.logo_id)} alt="" className="h-12 w-12 object-contain" />}
              </div>
              <div className="text-sm leading-8 [&_ol]:list-decimal [&_ol]:pr-5 [&_ul]:list-disc [&_ul]:pr-5" dangerouslySetInnerHTML={{ __html: cur.body_html }} />
              {cur.attachment_id && <a href={urlFor(cur.attachment_id)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs text-blue-700 underline"><Paperclip className="h-3.5 w-3.5" /> {cur.attachment_name || "المرفق"}</a>}
            </div>
            {cur.status !== "signed" && cur.require_signature
              ? <SignBox d={cur} onSigned={(r) => { setCur(r); load(); decisionPDFBlob(r, c, urlFor).then((b) => api.portalArchiveDecision(r.id, b)).catch(() => {}); }} />
              : cur.status === "signed" && <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700"><CheckCircle2 className="h-4 w-4" /> وقّعت هذا القرار {cur.biometric_verified ? "وتم التحقق ببصمتك" : ""}</p>}
            <Button data-testid="portal-decision-pdf" variant="outline" className="w-full gap-1.5" onClick={() => printDecision(cur, c, urlFor)}><Printer className="h-4 w-4" /> تحميل PDF</Button>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PortalLibrary = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { api.portalLibrary().then((r) => setItems(r.items || [])).catch(() => {}); }, []);
  const folders = [...new Set(items.map((x) => x.folder))];
  if (!items.length) return <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-400" data-testid="portal-library-empty">لا توجد ملفات منشورة</p>;
  return (
    <div className="space-y-4" data-testid="portal-library">
      {folders.map((fo) => (
        <div key={fo}>
          <p className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Folder className="h-4 w-4 text-blue-600" /> {fo}</p>
          <div className="space-y-2">{items.filter((x) => x.folder === fo).map((x) => (
            <a key={x.id} href={urlFor(x.file_id)} target="_blank" rel="noreferrer" data-testid={`portal-library-${x.id}`}
              className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm hover:border-blue-300">
              <Library className="h-5 w-5 text-blue-600" /><div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{x.name}</p>{x.description && <p className="truncate text-xs text-slate-500">{x.description}</p>}</div>
              <Download className="h-4 w-4 text-slate-400" />
            </a>))}</div>
        </div>
      ))}
    </div>
  );
};
